import { supabase } from '../../../shared/lib/supabase';
import { supabaseAuthAdmin, type CreateAuthUserInput } from '../../../shared/services/supabaseAuthAdmin';

// Define the User interface based on your actual DB schema
export interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  created_at: string;
  updated_at: string;
  active: boolean;
  global_role: string;
}

export interface UserCreateInput {
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  active?: boolean;
  global_role: string;
  password?: string;
  send_password_reset?: boolean;
}

export interface UserUpdateInput {
  email?: string;
  first_name?: string;
  last_name?: string;
  role?: string;
  active?: boolean;
  global_role?: string;
  password?: string;
}

class UsersService {
  async getAll(): Promise<User[]> {
    try {
      console.log('🔍 Fetching users...');
      
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('❌ Error fetching users:', error);
        throw error;
      }

      console.log('✅ Users fetched successfully:', data?.length || 0, 'records');
      return data || [];
    } catch (error) {
      console.error('❌ Users fetch error:', error);
      throw error;
    }
  }

  async getById(id: string): Promise<User | null> {
    try {
      console.log(`🔍 Fetching user by ID: ${id}`);
      
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        console.error('❌ Error fetching user:', error);
        throw error;
      }

      console.log('❌ User fetched successfully:', data);
      return data;
    } catch (error) {
      console.error('❌ User fetch error:', error);
      throw error;
    }
  }

  async create(data: UserCreateInput): Promise<User> {
    try {
      console.log('📝 Creating user with auth integration:', data);
      
      // Step 1: Create Supabase Auth user
      const authUserInput: CreateAuthUserInput = {
        email: data.email,
        password: data.password,
        email_confirm: true, // Auto-confirm email for admin-created users
        user_metadata: {
          first_name: data.first_name,
          last_name: data.last_name,
          role: data.role,
          global_role: data.global_role
        }
      };

      const authUser = await supabaseAuthAdmin.createUser(authUserInput);
      
      if (!authUser?.id) {
        throw new Error('Failed to create auth user');
      }

      // Step 2: Create database user with the same ID as auth user
      const dbUserData = {
        id: authUser.id, // Use auth user ID
        email: data.email,
        first_name: data.first_name,
        last_name: data.last_name,
        role: data.role,
        global_role: data.global_role,
        active: data.active !== false, // Default to true
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      const { data: result, error } = await supabase
        .from('users')
        .insert([dbUserData])
        .select()
        .single();

      if (error) {
        console.error('❌ Error creating database user, cleaning up auth user:', error);
        // Clean up auth user if database creation fails
        try {
          await supabaseAuthAdmin.deleteUser(authUser.id);
        } catch (cleanupError) {
          console.error('❌ Error cleaning up auth user:', cleanupError);
        }
        throw error;
      }

      // Step 3: Send password reset email if requested
      if (data.send_password_reset) {
        try {
          await supabaseAuthAdmin.sendPasswordReset(data.email);
          console.log('📧 Password reset email sent to:', data.email);
        } catch (emailError) {
          console.warn('⚠️ Failed to send password reset email:', emailError);
          // Don't fail the entire operation for email issues
        }
      }

      console.log('✅ User created successfully with auth integration:', result);
      return result;
    } catch (error) {
      console.error('❌ User creation error:', error);
      throw error;
    }
  }

  async update(id: string, data: UserUpdateInput): Promise<User> {
    try {
      console.log(`📝 Updating user ${id}:`, data);
      
      // Step 1: Update auth user if password is provided
      if (data.password) {
        try {
          await supabaseAuthAdmin.updateUserPassword(id, data.password);
          console.log('🔑 Auth user password updated successfully');
        } catch (authError) {
          console.error('❌ Error updating auth user password:', authError);
          throw authError;
        }
      }

      // Step 2: Update auth user metadata if user info changed
      const metadataChanged = data.first_name || data.last_name || data.role || data.global_role;
      if (metadataChanged) {
        try {
          const metadata: any = {};
          if (data.first_name) metadata.first_name = data.first_name;
          if (data.last_name) metadata.last_name = data.last_name;
          if (data.role) metadata.role = data.role;
          if (data.global_role) metadata.global_role = data.global_role;
          
          await supabaseAuthAdmin.updateUserMetadata(id, metadata);
          console.log('🔄 Auth user metadata updated successfully');
        } catch (authError) {
          console.error('❌ Error updating auth user metadata:', authError);
          throw authError;
        }
      }

      // Step 3: Update database user
      const dataWithTimestamp = {
        ...data,
        updated_at: new Date().toISOString()
      };

      // Remove password from database update (it's handled by auth)
      const { password, ...dbUpdateData } = dataWithTimestamp;

      const { data: result, error } = await supabase
        .from('users')
        .update(dbUpdateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('❌ Error updating database user:', error);
        throw error;
      }

      console.log('✅ User updated successfully:', result);
      return result;
    } catch (error) {
      console.error('❌ User update error:', error);
      throw error;
    }
  }

  async delete(id: string): Promise<void> {
    try {
      console.log(`🗑️ Deleting user with auth integration: ${id}`);
      
      // Step 1: Delete database user first
      const { error: dbError } = await supabase
        .from('users')
        .delete()
        .eq('id', id);

      if (dbError) {
        console.error('❌ Error deleting database user:', dbError);
        throw dbError;
      }

      // Step 2: Delete auth user
      try {
        await supabaseAuthAdmin.deleteUser(id);
        console.log('✅ Auth user deleted successfully');
      } catch (authError) {
        console.error('❌ Error deleting auth user:', authError);
        // Don't throw error here as database user is already deleted
        // Log the issue for manual cleanup if needed
        console.warn('⚠️ Database user deleted but auth user deletion failed. Manual cleanup may be required.');
      }

      console.log('✅ User deleted successfully');
    } catch (error) {
      console.error('❌ User deletion error:', error);
      throw error;
    }
  }
}

export const usersService = new UsersService();
